import { useCallback, useEffect, useState } from 'react';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ScreenWidth } from 'web-common/src/shared/styles';

/**
 * Returns the first preceding sibling element to the first
 * element in a list, if it exists.
 *
 * @param list - List of DOM elements to get preceding sibling of.
 */
const getPreviousElement = (list: Element[]): Element | null => {
  if (list.length === 0) {
    return null;
  }

  const sibling = list[0].previousElementSibling;
  return sibling instanceof HTMLElement ? sibling : null;
};

/**
 * Returns the first following sibling element to the last
 * element in a list, if it exists.
 *
 * @param list - List of DOM elements to get following sibling of.
 */
const getNextElement = (list: Element[]): Element | null => {
  if (list.length === 0) {
    return null;
  }

  const sibling = list[list.length - 1].nextElementSibling;
  return sibling instanceof HTMLElement ? sibling : null;
};

/**
 * Returns the number of selected preceding sibling elements to the first
 * element in a list.
 *
 * @param list - List of DOM elements to get count of selected preceding siblings of.
 */
const getPreviousSelectedCount = (list: Element[]): number => {
  if (list.length === 0) {
    return 0;
  }

  const element = list[0].previousElementSibling;
  if (!(element instanceof HTMLElement)) {
    return 0;
  }

  return (element.getAttribute('data-selected') === 'true' ? 1 : 0) + getPreviousSelectedCount([element]);
};

/**
 * Returns the number of selected following sibling elements to the last
 * element in a list.
 *
 * @param list - List of DOM elements to get count of selected following siblings of.
 */
const getNextSelectedCount = (list: Element[]): number => {
  if (list.length === 0) {
    return 0;
  }

  const element = list[list.length - 1].nextElementSibling;
  if (!(element instanceof HTMLElement)) {
    return 0;
  }

  return (element.getAttribute('data-selected') === 'true' ? 1 : 0) + getNextSelectedCount([element]);
};

export const useScrollSnapCarousel = (ref: HTMLDivElement | null, isVisibleOnSmallScreen?: boolean) => {
  const { nearestScreenWidth } = useScreenContext();
  const [previousElement, setPreviousElement] = useState<Element | null>(null);
  const [nextElement, setNextElement] = useState<Element | null>(null);
  const [numItemsSelectedOnLeft, setNumItemsSelectedOnLeft] = useState<number>(0);
  const [numItemsSelectedOnRight, setNumItemsSelectedOnRight] = useState<number>(0);
  const [refPositionX, setRefPositionX] = useState<number>(0);

  useEffect(() => {
    if (!ref || (nearestScreenWidth === ScreenWidth.SMALL && !isVisibleOnSmallScreen)) {
      return;
    }

    const update = () => {
      const rect = ref.getBoundingClientRect();
      setRefPositionX(rect.x);

      const visibleElements = Array.from(ref.children).filter((child) => {
        const childRect = child.getBoundingClientRect();

        // Rounding because Safari floating points create inaccuracies
        const leftEdgeIsVisible = Math.round(rect.left) <= Math.round(childRect.left);
        const rightEdgeIsVisible = Math.round(rect.right) >= Math.round(childRect.right);
        return leftEdgeIsVisible && rightEdgeIsVisible;
      });

      if (visibleElements.length > 0) {
        setPreviousElement(getPreviousElement(visibleElements));
        setNextElement(getNextElement(visibleElements));
        setNumItemsSelectedOnLeft(getPreviousSelectedCount(visibleElements));
        setNumItemsSelectedOnRight(getNextSelectedCount(visibleElements));
      }
    };

    update();
    ref.addEventListener('scroll', update, { passive: true });

    return () => ref.removeEventListener('scroll', update);

    // Track ref.clientWidth because there is a delay in
    // Chrome + Safari's calculation of getBoundingClientRect()
  }, [ref, ref?.clientWidth, nearestScreenWidth]);

  const scrollRight = useCallback(() => {
    if (!nextElement || !ref) return;
    const next = nextElement?.getBoundingClientRect();
    const diff = refPositionX - next.x;
    ref.scrollBy({ left: Math.abs(diff), behavior: 'smooth' });
  }, [ref, nextElement, refPositionX]);

  const scrollLeft = useCallback(() => {
    if (!previousElement || !ref) return;

    const prev = previousElement?.getBoundingClientRect();
    const diff = refPositionX - prev.x;
    ref.scrollBy({ left: -Math.abs(diff), behavior: 'smooth' });
  }, [ref, previousElement, refPositionX]);

  return {
    hasItemsOnLeft: previousElement !== null,
    hasItemsOnRight: nextElement !== null,
    scrollLeft,
    scrollRight,
    numItemsSelectedOnLeft,
    numItemsSelectedOnRight,
  };
};
